import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useTelemetry } from "../shared/useTelemetry";
import { normalizeAssignmentStatus } from "./assignmentStatus";
import type {
    AssignmentSummary,
    AssignmentCreateRequest,
    AssignmentUpdateRequest,
    AssignmentResponse,
    AddressCountRequest,
    AddressCountResponse
} from "./types";

const normalizeAssignmentResponse = (item: AssignmentResponse): AssignmentResponse => ({
    ...item,
    status: normalizeAssignmentStatus(item.status)
});

const fallbackAssignments: AssignmentSummary[] = [
    {
        id: "sample-1",
        name: "Stockholm Innerstad",
        ownerName: "Anna Svensson",
        remainingAddresses: 132,
        progress: 0.68
    },
    {
        id: "sample-2",
        name: "Uppsala Centrum",
        ownerName: "Björn Karlsson",
        remainingAddresses: 58,
        progress: 0.84
    }
];

export const fetchAssignments = async (): Promise<AssignmentSummary[]> => {
    try {
        const { data } = await apiClient.get<AssignmentSummary[]>(
            "/assignments/summary"
        );
        return data;
    } catch {
        return fallbackAssignments;
    }
};

export const getAssignments = async (
    campaignId?: string,
    distributorId?: string,
    municipalityId?: string
): Promise<AssignmentResponse[]> => {
    const params = new URLSearchParams();
    if (campaignId) params.append("campaignId", campaignId);
    if (distributorId) params.append("distributorId", distributorId);
    if (municipalityId) params.append("municipalityId", municipalityId);

    const queryString = params.toString();
    const url = `/assignments${queryString ? `?${queryString}` : ""}`;

    const { data } = await apiClient.get<{ items: AssignmentResponse[] }>(url);
    return data.items.map(normalizeAssignmentResponse);
};

/**
 * Gets active assignments for the current user
 */
export const getActiveAssignments = async (): Promise<AssignmentResponse[]> => {
    const { data } = await apiClient.get<{ items: AssignmentResponse[] }>("/assignments/active");
    return data.items.map(normalizeAssignmentResponse);
};

export const getAssignment = async (
    id: string
): Promise<AssignmentResponse> => {
    const { data } = await apiClient.get<AssignmentResponse>(`/assignments/${id}`);
    return normalizeAssignmentResponse(data);
};

export const createAssignment = async (
    request: AssignmentCreateRequest
): Promise<AssignmentResponse> => {
    const { data } = await apiClient.post<AssignmentResponse>(
        "/assignments",
        request
    );
    return normalizeAssignmentResponse(data);
};

export const updateAssignment = async (
    id: string,
    request: AssignmentUpdateRequest
): Promise<AssignmentResponse> => {
    const { data } = await apiClient.put<AssignmentResponse>(
        `/assignments/${id}`,
        request
    );
    return normalizeAssignmentResponse(data);
};

export const deleteAssignment = async (id: string): Promise<void> => {
    await apiClient.delete(`/assignments/${id}`);
};

/**
 * React Query mutation hook for creating a new assignment
 */
export const useCreateAssignment = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry(); return useMutation({
        mutationFn: createAssignment,
        onSuccess: () => {
            telemetry.track("assignment.created");
            // Invalidate map queries to refresh assignment polygons
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("assignment.failed", error);
        }
    });
};

/**
 * React Query mutation hook for updating an existing assignment
 */
export const useUpdateAssignment = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: ({ id, request }: { id: string; request: AssignmentUpdateRequest }) =>
            updateAssignment(id, request),
        onSuccess: () => {
            telemetry.track("assignment.updated");
            // Invalidate map queries to refresh assignment polygons
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("assignment.updateFailed", error);
        }
    });
};

/**
 * React Query mutation hook for deleting an assignment
 */
export const useDeleteAssignment = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: deleteAssignment,
        onSuccess: () => {
            telemetry.track("assignment.deleted");
            // Invalidate map queries to refresh assignment polygons
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("assignment.deleteFailed", error);
        }
    });
};

/**
 * Counts addresses within a polygon area
 */
export const countAddressesInArea = async (
    request: AddressCountRequest
): Promise<AddressCountResponse> => {
    try {
        const response = await apiClient.post<AddressCountResponse>(
            "/assignments/count-addresses",
            request,
            {
                timeout: 60000 // 60 second timeout for spatial queries
            }
        );
        return response.data;
    } catch (error: any) {
        console.error("[API] Failed to count addresses:", error);
        throw error;
    }
};